import {
  forwardRef,
  MouseEventHandler,
  RefForwardingComponent,
  useRef,
  useState,
} from 'react'
import { useUA } from 'context'
import gql from 'graphql-tag'
import { useRouter } from 'next/router'
import styled, { css } from 'styled-components'
import { NewsItem_NewsItemEdgeFragment } from 'generated/graphql'
import useReadNewsItemEffect from '@app.feature/channel/hook/useReadNewsItemEffect'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import { getWebviewHref } from '@app.modules/util'
import NewsItemCommentForm from '../component/NewsItemCommentForm/NewsItemCommentForm'
import NewsItemCommentList from '../component/NewsItemCommentList'
import NewsItemContent from '../component/NewsItemContent/NewsItemContent'
import NewsItemFooter from '../component/NewsItemFooter/NewsItemFooter'
import NewsItemHeader from '../component/NewsItemHeader/NewsItemHeader'
import ScreenNewsItemModal from './ScreenNewsItemModal/ScreenNewsItemModal'

gql`
  fragment NewsItem_newsItemEdge on NewsItemEdge {
    header: node {
      ...NewsItemHeader_NewsItem
    }
    content: node {
      ...NewsItemContent_NewsItem
    }
    footer: node {
      ...NewsItemFooter_NewsItem
    }
    commentForm: node {
      id
      viewerCanReply
    }
    commentList: node {
      id
      viewerIsAuthor
    }
    cursor
  }
`
type TProps = {
  data: Omit<NewsItem_NewsItemEdgeFragment, '__typename' | 'cursor'>
  isExpandContent?: boolean
  isExpandComment?: boolean
  isShowFollowBtn?: boolean
  className?: string
}

const ScreenNewsItemFeed: RefForwardingComponent<HTMLDivElement, TProps> = (
  {
    className,
    data,
    isExpandContent = false,
    isExpandComment = false,
    isShowFollowBtn = false,
  },
  ref,
) => {
  const router = useRouter()
  const [commentBtnState, setCommentBtnState] = useState(isExpandComment)
  const { isMobile } = useUA()
  const isWebview = useIsWebview()
  const target = useReadNewsItemEffect(data.header?.id)
  const commentInputRef = useRef<HTMLTextAreaElement>(null)

  const handleModal = (modal: string) => {
    router.push(
      {
        query: {
          ...router.query,
          modal,
        },
      },
      undefined,
      {
        scroll: false,
      },
    )
  }

  const handleCommentBtnClick = () => {
    if (isExpandComment) {
      if (isMobile) {
        commentInputRef.current?.focus()
      }
      return
    }
    if (isMobile) {
      router.push(
        getWebviewHref(
          `/channel/${data.header?.channel?.url}/${data.header?.id}`,
          isWebview,
        ),
      )
    } else {
      setCommentBtnState(!commentBtnState)
    }
  }

  const handleContentClick: MouseEventHandler<HTMLDivElement> = (e) => {
    const targetNode = e.target
    if (!isMobile || targetNode instanceof HTMLAnchorElement) return
    router.push(
      getWebviewHref(
        `/channel/${data.header?.channel?.url}/${data.header?.id}`,
        isWebview,
      ),
    )
  }

  return (
    <StyledWrapper ref={ref} className={className}>
      <div ref={target} />
      <NewsItemHeader data={data.header!} showFollowBtn={isShowFollowBtn} />
      <NewsItemContent
        data={data.content!}
        onImageClick={() => handleModal(data.header?.id || '')}
        onContentClick={handleContentClick}
        expendContent={isExpandContent}
      />
      <NewsItemFooter
        data={data.footer!}
        isCommentListOpened={commentBtnState}
        onCommentBtnClick={handleCommentBtnClick}
      />
      {commentBtnState === true && (
        <>
          <NewsItemCommentForm
            className="comment-form"
            newsItemId={data.commentForm?.id || ''}
            viewerCanReply={data.commentForm?.viewerCanReply || false}
            ref={commentInputRef}
            expendComment={isExpandComment}
          />
          <NewsItemCommentList
            newsItemId={data.commentList?.id || ''}
            viewerIsAuthor={data.commentList?.viewerIsAuthor || false}
            expendComment={isExpandComment}
          />
        </>
      )}
      {router.query.modal === data.header?.id && (
        <ScreenNewsItemModal
          open={true}
          data={data}
          onClose={() => handleModal('close')}
        />
      )}
    </StyledWrapper>
  )
}

export default forwardRef(ScreenNewsItemFeed)

const StyledWrapper = styled.div`
  background-color: white;
  border: 1px solid #e8e8e8;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  overflow: visible;
  .desktop {
    display: block;
  }
  .mobile {
    display: none;
  }
  & > .hr {
    height: 1px;
    background-color: #e8e8e8;
    margin-bottom: 20px;
  }
  > .comment-form {
    padding: 16px 20px;
  }

  ${({ theme }) =>
    theme.isMobile &&
    css`
      border: none;
      .desktop {
        display: none;
      }
      .mobile {
        display: block;
      }
    `}
`
